import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type FormModalProps = {
  open: boolean;
  id: 'feedback' | 'name-suggestion';
  title: string;
  intro: string;
  children: ReactNode;
};

function focusableElements(container: HTMLElement | null) {
  return Array.from(
    container?.querySelectorAll<HTMLElement>(
      'button:not(:disabled),a[href],input:not(:disabled),textarea:not(:disabled),select:not(:disabled),[tabindex="0"]',
    ) ?? [],
  ).filter(element => element.getClientRects().length > 0);
}

/** Shared portal, focus, and scroll-lock behavior for the temporary beta forms. */
export function FormModal({ open, id, title, intro, children }: FormModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const titleId = `twe-asa-form-title-${id}`;

  useLayoutEffect(() => {
    if (!open) return;

    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;

    const focusFrame = requestAnimationFrame(() => {
      const target = dialogRef.current?.querySelector<HTMLElement>('[data-form-autofocus]') ?? focusableElements(dialogRef.current)[0];
      target?.focus({ preventScroll: true });
    });

    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.key !== 'Tab') return;
      const items = focusableElements(dialogRef.current);
      const first = items[0];
      const last = items.at(-1);
      if (!first || !last) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };

    const onFocusIn = (event: FocusEvent) => {
      if (!dialogRef.current?.contains(event.target as Node)) {
        (focusableElements(dialogRef.current)[0] ?? dialogRef.current).focus();
      }
    };

    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', onKeydown, true);
      document.removeEventListener('focusin', onFocusIn);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      if (openerRef.current?.isConnected) openerRef.current.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) openerRef.current = null;
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="twe-asa-beta-forms twe-asa-form-backdrop">
      <div ref={dialogRef} className="twe-asa-form-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className="account-form__heading-container">
          <h2 id={titleId} className="account-font__heading-m">{title}</h2>
          <p className="account-font__body-s">{intro}</p>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
